import { supabase } from "@/lib/supabase";
import { USER_DIRECTORY_FIELDS, USERS_DIRECTORY_VIEW } from "@/lib/userAccess";

// Indica si un cliente tiene la firma SEPA informada (no nula y no vacia).
export function hasSepaSignature(customer) {
  const value = customer?.sepa_signature;
  return typeof value === "string" && value.trim() !== "";
}

function getCustomerCode(customer) {
  const value = customer?.code;
  return value == null || String(value).trim() === "" ? "Nuevo_cliente" : String(value);
}

function toNumber(value) {
  const number = Number(value);
  return Number.isFinite(number) ? number : 0;
}

function sumOrderTotals(orders) {
  if (!Array.isArray(orders)) return 0;

  const seenOrders = new Set();
  const total = orders.reduce((sum, order) => {
    if (!order?.id || seenOrders.has(order.id)) return sum;
    seenOrders.add(order.id);
    return sum + toNumber(order.total);
  }, 0);

  return Math.round(total * 100) / 100;
}

export async function fetchCustomerRepresentatives() {
  try {
    const { data, error } = await supabase
      .from(USERS_DIRECTORY_VIEW)
      .select(USER_DIRECTORY_FIELDS)
      .not("cod_rep", "is", null)
      .order("name", { ascending: true });

    if (error) throw error;

    const representativesByCode = new Map();
    (data || []).forEach((representative) => {
      const codRep = String(representative.cod_rep || "").trim();
      if (!codRep || representativesByCode.has(codRep)) return;
      representativesByCode.set(codRep, {
        codRep,
        name: representative.name || codRep,
      });
    });

    const representatives = Array.from(representativesByCode.values()).sort((a, b) => {
      const nameCompare = a.name.localeCompare(b.name, "es");
      return nameCompare || a.codRep.localeCompare(b.codRep, "es");
    });

    return { data: representatives, error: null };
  } catch (error) {
    console.error("Customer representatives error:", error);
    return { data: [], error };
  }
}

// Devuelve el listado de clientes con el estado de la firma SEPA.
// sepaFilter: "all" | "with" | "without"
// onlyWithOrders: si es true, solo incluye clientes con al menos un pedido.
// orderSeason: si se informa, solo incluye clientes con pedidos que contengan articulos de esa temporada.
// includeOrderTotals: si es true, incluye el importe acumulado de los pedidos seleccionados.
// representativeCode: si se informa, filtra clientes por customers.cod_rep.
export async function fetchCustomersSepaStatus({
  searchTerm = "",
  sepaFilter = "all",
  onlyWithOrders = false,
  orderSeason = "",
  includeOrderTotals = false,
  representativeCode = "",
} = {}) {
  try {
    const season = orderSeason.trim();
    const selectedRepresentativeCode = representativeCode.trim();
    const customerColumns = "id, name, email, phone, cif, code, cod_rep, sepa_signature";
    const ordersColumns = season
      ? ", orders!inner(id, total, order_items!inner(id, products!inner(temporada)))"
      : onlyWithOrders
        ? ", orders!inner(id, total)"
        : includeOrderTotals
          ? ", orders(id, total)"
          : "";
    const selectColumns = `${customerColumns}${ordersColumns}`;

    let query = supabase
      .from("customers")
      .select(selectColumns)
      .order("name", { ascending: true })
      .order("cif", { ascending: true });

    if (season) {
      query = query.eq("orders.order_items.products.temporada", season);
    }

    if (selectedRepresentativeCode) {
      query = query.eq("cod_rep", selectedRepresentativeCode);
    }

    const term = searchTerm.trim();
    if (term) {
      const like = `%${term}%`;
      query = query.or(
        `name.ilike.${like},cif.ilike.${like},code.ilike.${like},phone.ilike.${like},email.ilike.${like},cod_rep.ilike.${like}`,
      );
    }

    const { data, error } = await query;
    if (error) throw error;

    let rows = (data || []).map((customer) => ({
      id: customer.id,
      name: customer.name || "",
      cif: customer.cif || "",
      code: getCustomerCode(customer),
      codRep: customer.cod_rep || "",
      phone: customer.phone || "",
      email: customer.email || "",
      orderTotal: includeOrderTotals ? sumOrderTotals(customer.orders) : 0,
      hasSepa: hasSepaSignature(customer),
    }));

    if (sepaFilter === "with") {
      rows = rows.filter((row) => row.hasSepa);
    } else if (sepaFilter === "without") {
      rows = rows.filter((row) => !row.hasSepa);
    }

    return { data: rows, error: null };
  } catch (error) {
    console.error("Customers SEPA status error:", error);
    return { data: [], error };
  }
}
